import React, { Component } from 'react'
import {
    Card, Table, Button, Modal, Form, Input, Select, Tag, Space, Alert,
    Popconfirm, message, DatePicker, Typography
} from 'antd'
import {
    getApiCredentialList, createApiCredential,
    disableApiCredential, enableApiCredential, deleteApiCredential,
    getUserListForDropbox
} from '../../request'
import { PlusOutlined, StopOutlined, CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons'
import moment from 'moment'

const { Text } = Typography

const SCOPE_OPTIONS = [
    { label: 'mcp:invoke', value: 'mcp:invoke' },
    { label: 'api:*', value: 'api:*' },
    { label: 'api:order.read', value: 'api:order.read' },
    { label: 'api:order.write', value: 'api:order.write' },
]

/**
 * API Key 管理
 * onlyMine: 为 true 时仅展示当前用户自己的 key，隐藏用户筛选器（个人中心场景）
 * fixedUserId: 锁定用户ID（onlyMine=true 时由父组件传入）
 *
 * 后端接口约定：
 *   - userId 为空  → 查询全部（管理员场景，后端需校验调用方具有管理员权限）
 *   - userId 有值  → 按该 userId 过滤（后端需校验调用方是该用户本人或管理员）
 */
export default class ApiKeyManagement extends Component {
    formRef = React.createRef()

    state = {
        dataSource: [],
        userList: [],
        columns: [],
        loading: false,
        creating: false,
        createVisible: false,
        newKeyVisible: false,
        newKeyValue: '',
        formParam: {
            currentPage: 1,
            pageSize: 20,
        },
        total: 0,
    }

    componentDidMount() {
        const { fixedUserId } = this.props
        // columns 依赖 onlyMine prop，初始化一次存入 state，避免每次 render 重建
        this.setState({ columns: this.buildColumns() })
        if (fixedUserId) {
            this.setState(
                { formParam: { ...this.state.formParam, userId: fixedUserId } },
                () => this.initList()
            )
        } else {
            this.initList()
            this.initUserList()
        }
    }

    normalizeListResponse = (res) => {
        if (Array.isArray(res)) {
            return { list: res, total: res.length }
        }
        if (Array.isArray(res?.list)) {
            return { list: res.list, total: res.total || res.list.length }
        }
        if (Array.isArray(res?.records)) {
            return { list: res.records, total: res.total || res.records.length }
        }
        if (Array.isArray(res?.data)) {
            return { list: res.data, total: res.total || res.data.length }
        }
        if (Array.isArray(res?.data?.list)) {
            return { list: res.data.list, total: res.data.total || res.total || res.data.list.length }
        }
        if (Array.isArray(res?.data?.records)) {
            return { list: res.data.records, total: res.data.total || res.total || res.data.records.length }
        }
        return { list: [], total: 0 }
    }

    initList = () => {
        this.setState({ loading: true })
        getApiCredentialList(this.state.formParam)
            .then(res => {
                const { list, total } = this.normalizeListResponse(res)
                this.setState({
                    loading: false,
                    dataSource: list,
                    total,
                })
            })
            .catch(() => this.setState({ loading: false }))
    }

    initUserList = () => {
        getUserListForDropbox({ type: 0 }).then(res => {
            this.setState({ userList: res || [] })
        })
    }

    handleCreate = (values) => {
        const { fixedUserId } = this.props
        const param = {
            name: values.name,
            scopes: values.scopes,
            expireAt: values.expireAt ? values.expireAt.format('YYYY-MM-DD HH:mm:ss') : null,
        }
        if (fixedUserId) param.userId = fixedUserId
        this.setState({ creating: true })
        createApiCredential(param)
            .then(res => {
                this.formRef.current.resetFields()
                this.setState({
                    creating: false,
                    createVisible: false,
                    newKeyValue: res?.fullKey || res?.data?.fullKey || '',
                    newKeyVisible: true,
                })
                this.initList()
            })
            .catch(() => this.setState({ creating: false }))
    }

    handleDisable = (record) => {
        disableApiCredential({ id: record.id }).then(() => {
            message.success('已禁用')
            this.initList()
        })
    }

    handleEnable = (record) => {
        enableApiCredential({ id: record.id }).then(() => {
            message.success('已启用')
            this.initList()
        })
    }

    handleDelete = (record) => {
        deleteApiCredential({ id: record.id }).then(() => {
            message.success('已删除')
            this.initList()
        })
    }

    buildColumns = () => {
        const { onlyMine } = this.props
        const cols = [
            {
                title: '名称',
                dataIndex: 'name',
                key: 'name',
                width: 150,
            },
            {
                title: 'Key前缀',
                dataIndex: 'keyDisplay',
                key: 'keyDisplay',
                width: 160,
                render: (text) => <Text code>{text}</Text>,
            },
            {
                title: 'Scope',
                dataIndex: 'scopes',
                key: 'scopes',
                render: (scopes) => {
                    if (!scopes) return '-'
                    let list = []
                    try {
                        list = Array.isArray(scopes) ? scopes : JSON.parse(scopes)
                    } catch (e) {
                        return <Text type="secondary">{String(scopes)}</Text>
                    }
                    return list.map(s => <Tag key={s} color="blue">{s}</Tag>)
                },
            },
            {
                title: '状态',
                dataIndex: 'status',
                key: 'status',
                width: 90,
                render: (status) =>
                    status === 1
                        ? <Tag color="success" icon={<CheckCircleOutlined />}>启用</Tag>
                        : <Tag color="default" icon={<StopOutlined />}>禁用</Tag>,
            },
            {
                title: '最近使用',
                dataIndex: 'lastUsedAt',
                key: 'lastUsedAt',
                width: 170,
                render: (t) => t ? moment(t).format('YYYY-MM-DD HH:mm:ss') : '-',
            },
            {
                title: '过期时间',
                dataIndex: 'expireAt',
                key: 'expireAt',
                width: 170,
                render: (t) => t ? moment(t).format('YYYY-MM-DD HH:mm:ss') : '永不过期',
            },
            {
                title: '创建时间',
                dataIndex: 'createdAt',
                key: 'createdAt',
                width: 170,
                render: (t) => t ? moment(t).format('YYYY-MM-DD HH:mm:ss') : '-',
            },
        ]
        if (!onlyMine) {
            cols.splice(2, 0, {
                title: '所属用户',
                dataIndex: 'userNick',
                key: 'userNick',
                width: 120,
            })
        }
        cols.push({
            title: '操作',
            key: 'actions',
            fixed: 'right',
            width: 160,
            render: (_, record) => (
                <Space size={4}>
                    {record.status === 1 ? (
                        <Popconfirm title="确认禁用该Key？" onConfirm={() => this.handleDisable(record)}>
                            <Button size="small" danger>禁用</Button>
                        </Popconfirm>
                    ) : (
                        <Popconfirm title="确认启用该Key？" onConfirm={() => this.handleEnable(record)}>
                            <Button size="small" type="primary" ghost>启用</Button>
                        </Popconfirm>
                    )}
                    <Popconfirm title="确认删除该Key？删除后不可恢复" onConfirm={() => this.handleDelete(record)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        })
        return cols
    }

    render() {
        const { onlyMine } = this.props
        const { dataSource, columns, loading, creating, createVisible, newKeyVisible, newKeyValue, userList, total } = this.state
        const hasKey = total > 0 || (dataSource || []).length > 0

        return (
            <div>
                <Card
                    title="API Key 管理"
                    bordered={false}
                    extra={
                        <Button type="primary" icon={<PlusOutlined />} disabled={loading || creating || hasKey} onClick={() => this.setState({ createVisible: true })}>
                            创建 API Key
                        </Button>
                    }
                >
                    {newKeyVisible && newKeyValue && (
                        <Alert
                            type="success"
                            showIcon
                            closable
                            onClose={() => this.setState({ newKeyVisible: false, newKeyValue: '' })}
                            message="API Key 创建成功"
                            description={(
                                <div style={{ marginTop: 8 }}>
                                    <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, marginBottom: 12 }}>
                                        <Text copyable code style={{ wordBreak: 'break-all' }}>{newKeyValue}</Text>
                                    </div>
                                    <Text type="danger">请立即复制并妥善保存，此 Key 只显示一次，关闭后将无法再次查看。</Text>
                                </div>
                            )}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {/* 管理员模式显示用户筛选 */}
                    {!onlyMine && (
                        <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                            <Select
                                style={{ width: 220 }}
                                showSearch
                                allowClear
                                placeholder="按用户筛选"
                                optionFilterProp="children"
                                fieldNames={{ label: 'USER_NICK', value: 'USER_ID' }}
                                filterOption={(input, option) =>
                                    ((option?.USER_NICK ?? '') + (option?.pinyin ?? '')).toLowerCase().includes(input.toLowerCase())
                                }
                                options={userList}
                                onChange={(val) => {
                                    this.setState(
                                        { formParam: { ...this.state.formParam, userId: val, currentPage: 1 } },
                                        this.initList
                                    )
                                }}
                            />
                            <Button onClick={this.initList}>刷新</Button>
                        </div>
                    )}

                    <Table
                        bordered
                        size="small"
                        rowKey="id"
                        dataSource={dataSource}
                        columns={columns}
                        loading={loading}
                        scroll={{ x: onlyMine ? 900 : 1100 }}
                        pagination={{
                            size: 'small',
                            current: this.state.formParam.currentPage,
                            pageSize: this.state.formParam.pageSize,
                            total,
                            showSizeChanger: true,
                            onChange: (page, pageSize) => {
                                this.setState(
                                    { formParam: { ...this.state.formParam, currentPage: page, pageSize } },
                                    this.initList
                                )
                            },
                        }}
                    />
                </Card>

                {/* 创建 Key 弹窗 */}
                <Modal
                    title="创建 API Key"
                    visible={createVisible}
                    onCancel={() => { this.setState({ createVisible: false }); this.formRef.current?.resetFields() }}
                    footer={null}
                    destroyOnClose
                >
                    <Form
                        ref={this.formRef}
                        layout="vertical"
                        onFinish={this.handleCreate}
                    >
                        <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入Key名称' }]}>
                            <Input placeholder="例如：MCP调用Key" maxLength={50} />
                        </Form.Item>
                        <Form.Item name="scopes" label="权限范围(Scope)" rules={[{ required: true, message: '请选择至少一个Scope' }]}>
                            <Select mode="multiple" placeholder="请选择Scope" options={SCOPE_OPTIONS} />
                        </Form.Item>
                        <Form.Item name="expireAt" label="过期时间（可选，不填则永不过期）">
                            <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm:ss" />
                        </Form.Item>
                        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                            <Space>
                                <Button onClick={() => { this.setState({ createVisible: false }); this.formRef.current?.resetFields() }}>取消</Button>
                                <Button type="primary" htmlType="submit" loading={creating}>创建</Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        )
    }
}
